import React from "react";
import type { ReactNode } from "react";
import { useAuth } from "tecrmi_components/useAuth";
import { setGlobalAccessTokenFn } from "@utils/httpClient";
import { LoadingScreen } from "@xplatform/design-system";
import { setAccessToken, clearAccessToken, setAccessTokenRefresher } from "@utils/tokenStore";

interface Props {
    children: ReactNode;
}

export const AdminContextProvider: React.FC<Props> = ({ children }) => {
    const { getValidAccessToken, currentSession, loading } = useAuth();
    const [tokenReady, setTokenReady] = React.useState(false);

    // Keep a ref so effects always call the latest version without
    // re-running every time the external hook recreates the function.
    const getValidAccessTokenRef = React.useRef(getValidAccessToken);
    React.useEffect(() => {
        getValidAccessTokenRef.current = getValidAccessToken;
        setGlobalAccessTokenFn(getValidAccessToken);
        setAccessTokenRefresher(getValidAccessToken);
    }, [getValidAccessToken]);

    React.useEffect(() => {
        const storeToken = async () => {
            const token = await getValidAccessTokenRef.current();
            if (token) {
                setAccessToken(token);
                setTokenReady(true);
            }
        };
        if (currentSession) {
            storeToken();
        } else {
            clearAccessToken();
            setTokenReady(false);
        }
    }, [currentSession]); // only re-run when session changes, not on every function identity change

    if (loading || (currentSession && !tokenReady)) {
        return <LoadingScreen message="Checking authentication..." />;
    }

    return <>{children}</>;
};
